<template>
    <Modal
        @close="cancelEdit">
        <template slot="header">{{ siteContent.editProfile }}</template>
        <template slot="body">
            <div class="images">
                <div class="coverWrapper">
                    <img class="cover"
                        :src="cover"/>
                    <div class="changeCover">
                        <input class="fileUploadOriginal"
                            accept="image/jpeg"
                            type="file"
                            name="cover"
                            id="cover"
                            tabindex="-1"
                            @change="uploadCover"/>

                        <div class="editCover"
                            v-on-clickaway="closeCoverDropdown">
                            <Button
                                appearance="secondary"
                                prefixIcon="edit"
                                size="small"
                                @click="coverDropdown=!coverDropdown">
                                {{ siteContent.editCover }}
                            </Button>
                            <transition name="dropdown">
                                <Dropdown
                                    v-if="coverDropdown"
                                    :elements="coverElements"
                                    @deleteCover="deleteCover">
                                    <template slot="0">
                                        <Icon class="buttonIcon"
                                            icon="upload"
                                            appearance="inherit"/>
                                        <label class="uploadLabel"
                                            for="cover"
                                            tabindex="0">
                                            {{ siteContent.uploadCover }}
                                        </label>
                                    </template>
                                    <template slot="1">
                                        <Icon class="buttonIcon"
                                            icon="delete"
                                            appearance="inherit"/>
                                        {{ siteContent.deleteCover }}
                                    </template>
                                </Dropdown>
                            </transition>
                        </div>
                    </div>
                </div>
                <div class="avatarWrapper">
                    <img class="avatar"
                        :src="avatar"/>
                        <div class="editAvatar"
                            v-on-clickaway="closeAvatarDropdown">
                            <input class="fileUploadOriginal"
                                accept="image/jpeg"
                                type="file"
                                name="avatar"
                                id="avatar"
                                tabindex="-1"
                                @change="uploadAvatar"/>
                            <Button class="editAvatarButton"
                                appearance="secondary"
                                prefixIcon="edit"
                                size="small"
                                @click="avatarDropdown=!avatarDropdown">
                            </Button>
                            <transition name="dropdown">
                                <Dropdown
                                    v-if="avatarDropdown"
                                    :elements="avatarElements"
                                    @deleteAvatar="deleteAvatar">
                                    <template slot="0">
                                        <Icon class="buttonIcon"
                                            icon="upload"
                                            appearance="inherit"/>
                                        <label class="uploadLabel"
                                            for="avatar"
                                            tabindex="0">
                                            {{ siteContent.uploadAvatar }}
                                        </label>
                                    </template>
                                    <template slot="1">
                                        <Icon class="buttonIcon"
                                            icon="delete"
                                            appearance="inherit"/>
                                        {{ siteContent.deleteAvatar }}
                                    </template>
                                </Dropdown>
                            </transition>
                        </div>
                </div>
            </div>
            <TextField class="field"
                v-model="name"
                :label="siteContent.name"
                :maxlength="constants.limits.username.max"
                :invalid="isUsernameInvalid"
                id="name"
                ref="name"
                size="large"
                font="secondary">
            </TextField>
            <TextFieldError class="imageError"
                v-if="isUsernameInvalid">
                {{ siteContent.invalidUsername }}
            </TextFieldError>
            <TextField class="field"
                v-model="intro"
                :label="siteContent.intro"
                :maxlength="250"
                :isCharCountOn="true"
                id="intro"
                ref="intro"
                type="textarea">{{ intro }}</TextField>
            <div class="socialFields">
                <TextField class="instagramField iconContainer field"
                    v-model="instagram"
                    :label="siteContent.instagram"
                    id="instagram"
                    ref="instagram"
                    size="medium">
                </TextField>
                <TextField class="webField iconContainer field"
                    v-model="website"
                    :label="siteContent.website"
                    id="website"
                    ref="website"
                    size="medium">
                </TextField>
            </div>
        </template>
        <template class="updateButton"
            slot="footer">
            <Button
                prefixIcon="file_upload"
                @click="update">
                {{ siteContent.update }}
            </Button>
        </template>
    </Modal>
</template>

<script>
    import { mapActions } from 'vuex'
    import { mixin as clickaway } from 'vue-clickaway'

    export default {
        name: 'EditProfileModal',
        mixins: [ clickaway ],
        props: {
            currentUser: {
                type: Object,
                required: true
            },
            id: {
                type: String
            }
        },
        data() {
            return {
                originalAvatar: this.currentUser.avatar,
                originalCover: this.currentUser.cover,
                coverPayload: [],
                avatarPayload: [],
                updatedCover: '',
                updatedAvatar: '',
                name: this.currentUser.name,
                isUsernameInvalid: false,
                intro: this.currentUser.intro,
                instagram: this.currentUser.instagram,
                website: this.currentUser.website,
                coverDropdown: false,
                coverElements: [
                    {
                        action: 'uploadNewCover'
                    },
                    {
                        action: 'deleteCover'
                    }
                ],
                avatarDropdown: false,
                avatarElements: [
                    {
                        action: 'uploadNewAvatar'
                    },
                    {
                        action: 'deleteAvatar'
                    }
                ]
            }
        },
        computed: {
            avatar() {
                if(this.updatedAvatar) {
                    return this.updatedAvatar
                } else if(this.originalAvatar) {
                    return this.originalAvatar
                } else {
                    return this.constants.defaults.avatar
                }
            },
            cover() {
                if(this.updatedCover) {
                    return this.updatedCover
                } else if(this.originalCover) {
                    return this.originalCover
                } else {
                    return this.constants.defaults.cover
                }
            },
            nameTags() {
                // Generate tags for search by name
                var nameArray = this.name.toLowerCase().split(' ')
                var nameTags = []

                for(let i = 0; i < nameArray.length; i++) {
                    for(let j = 2; j < nameArray[i].length + 1; j++) {
                        nameTags.push(nameArray[i].substring(0, j))
                    }
                }

                nameTags.push(this.name.toLowerCase())

                return nameTags
            }
        },
        methods: {
            ...mapActions('users',['updateProfile','uploadFileToStore']),

            async uploadCover(e) {
                var files = e.target.files || e.dataTransfer.files
                var fileSize = e.target.files[0].size / 1024 / 1024 // size in MB

                if(fileSize) {
                    if (fileSize > 0.4) {
                        this.$toasted.show(this.siteContent.keepFilesUnderLimit)
                        return
                    } else if (!files.length) {
                        return
                    } else {
                        await this.createCoverPreview(files[0])
                        var file = files[0]
                        var filePath = 'users/' + this.currentUser.id + '/cover/' + file.name.replace(/\.[^/.]+$/, "")  + '.jpg'
                        
                        this.payload = {
                            file: file,
                            filePath: filePath
                        }

                        console.log(this.payload)
                    }
                }

                this.closeCoverDropdown()
            },

            createCoverPreview(file) {
                var reader = new FileReader()
                reader.readAsDataURL(file)

                reader.onload = (e) => {
                    var image = e.target.result
                    this.updatedCover = image
                }
            },

            async uploadAvatar(e) {
                var files = e.target.files || e.dataTransfer.files
                var fileSize = e.target.files[0].size / 1024 / 1024 // size in MB

                if(fileSize) {
                    if (fileSize > 0.12) {
                        this.$toasted.show(this.siteContent.keepFilesUnderLimit)
                        return
                    } else if (!files.length) {
                        return
                    } else {
                        this.createAvatarPreview(files[0])
                        var file = files[0]
                        var filePath = 'users/' + this.currentUser.id + '/avatar/' + file.name.replace(/\.[^/.]+$/, "")  + '.jpg'
                        
                        this.avatarPayload = {
                            file: file,
                            filePath: filePath
                        }

                        console.log(this.avatarPayload)
                    }
                }

                this.closeAvatarDropdown()
            },

            createAvatarPreview(file) {
                var reader = new FileReader()
                reader.readAsDataURL(file)

                reader.onload = (e) => {
                    var image = e.target.result
                    this.updatedAvatar = image
                }
            },

            async update() {
                try {
                    if(this.updatedCover) {
                        var cover = await this.uploadFileToStore(this.payload)
                    } else if(!this.originalCover) {
                        var cover = ''
                    } else {
                        var cover = this.originalCover
                    }

                    if(this.updatedAvatar) {
                        var avatar = await this.uploadFileToStore(this.avatarPayload)
                    } else if(!this.originalAvatar) {
                        var avatar = ''
                    } else {
                        var avatar = this.originalAvatar
                    }
                    
                    const user = {
                        avatar:    avatar,
                        cover:     cover,
                        name:      this.name,
                        nameTags:  this.nameTags,
                        intro:     this.intro,
                        instagram: this.instagram,
                        website:   this.website
                    }

                    var userParams = {
                        id: this.id,
                        userData: user
                    }

                    var hasNewCover
                    if(this.updatedCover && this.originalCover) {
                        hasNewCover = true
                    } else {
                        hasNewCover = false
                    }

                    var hasNewAvatar
                    if(this.updatedAvatar && this.originalAvatar) {
                        hasNewAvatar = true
                    } else {
                        hasNewAvatar = false
                    }

                    if(this.name.length < this.constants.limits.username.min || 
                        this.name.length > this.constants.limits.username.max) {
                            this.$emit('setInvalid', this.siteContent.invalidUsername)
                            this.isUsernameInvalid = true
                    } else {
                        var result = await this.updateProfile({
                            params:         userParams,
                            originalCover:  this.originalCover,
                            hasNewCover:    hasNewCover,
                            originalAvatar: this.originalAvatar,
                            hasNewAvatar:   hasNewAvatar
                        })

                        if(result) {
                            this.$emit('updateProfile', user)
                            this.$toasted.show(this.siteContent.profileUpdateSuccess)
                            this.$emit('close')
                        } else {
                            this.$toasted.show(this.siteContent.profileUpdateError)
                        }
                    }
                } catch(err) {
                    console.log(err)
                }
            },
            forceUpdateTextFields() {
                const intro = this.$refs.intro.$el.querySelector('textarea')
                const event = new Event('keyup')

                intro.dispatchEvent(event)
            },
            cancelEdit() {
                this.currentUser.avatar = this.currentUser.avatar
                this.currentUser.cover  = this.currentUser.cover
                this.$emit('close')
            },
            deleteCover() {
                this.originalCover = ''
                this.updatedCover = ''
                this.closeCoverDropdown()
            },
            deleteAvatar() {
                this.originalAvatar = ''
                this.updatedAvatar = ''
                this.closeAvatarDropdown()
            },
            closeAvatarDropdown() {
                this.avatarDropdown = false
            },
            closeCoverDropdown() {
                this.coverDropdown = false
            },
        },
        async mounted() {
            this.forceUpdateTextFields()
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../scss/variables';

    .dropdown-enter-active, .dropdown-leave-active {
        transition: $transition-eio-20;
        opacity: 1;
        transform: translateY(0);
    }

    .dropdown-enter, .dropdown-leave-to {
        opacity: 0;
        transform: translateY(-3rem);
    }

    .fileUploadOriginal {
        width: 0.1px;
        height: 0.1px;
        opacity: 0;
        overflow: hidden;
        position: absolute;
        z-index: -1;
    }

    .images {
        position: relative;
        margin-bottom: 8rem;
    }
    
    .cover {
        width: 100%;
        height: 20rem;
        object-fit: cover;
        display: block;
        border-radius: 1.5rem;
        background: var(--color-grey-200);
    }

    .avatarWrapper {
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%) translateY(50%);
    }

    .avatar {
        width: 10rem;
        height: 10rem;
        object-fit: cover;
        display: block;
        border-radius: 50%;
        border: 0.4rem solid var(--color-white);
        background: var(--color-grey-200);
    }

    .deleteAvatar {
        position: absolute;
        top: -1rem;
        right: -1rem;
    }

    .editAvatar {
        position: absolute;
        top: -0.5rem;
        left: -0.5rem;
        display: flex;
        flex-direction: column;
    }

    .editAvatarButton {
        padding: 0.75rem 0.25rem 0.75rem 0.75rem !important;
    }

    .changeCover {
        position: absolute;
        width: 100%;
        top: 0;
        display: flex;
        justify-content: space-between;
        padding: 1rem;
    }

    .editCover {
        display: flex;
        flex-direction: column;
    }

    .editIcon {
        margin-right: 0.5rem;
    }

    .field {
        margin-bottom: 1.5rem;
    }

    .socialFields {
        display: flex;
        width: 100%;

        .field {
            margin-right: 1rem;

            &:last-of-type {
                margin-right: 0;
            }
        }
    }

    .uploadLabel {
        cursor: pointer;
    }

    .buttonIcon {
        margin-right: 1rem;
    }

    @media all and (max-width: 640px) {
        .cover {
            height: 20rem;
        }

        .socialFields {
            flex-direction: column;
        }
    }
</style>